<template>
	<div class="assistant-list">
		<div class="assistant-list-toolbar">
			<v-text-field
				v-model="search"
				dense
				outlined
				hide-details
				clearable
				placeholder="Search titles"
				aria-label="Search conversation titles"
				prepend-inner-icon="mdi-magnify"
				@input="debouncedSearch"
			></v-text-field>
		</div>
		<v-progress-linear v-if="loading" indeterminate color="primary"></v-progress-linear>
		<div v-if="!loading && conversations.length === 0" class="assistant-list-empty">
			{{ search ? "No conversations match that search." : "No conversations yet." }}
		</div>
		<v-list dense class="py-0">
			<v-list-item
				v-for="c in conversations"
				:key="c.conversationId"
				:class="{
					'assistant-list-current': c.conversationId === currentId,
					'assistant-list-confirming': confirmingId === c.conversationId,
				}"
				:ripple="confirmingId !== c.conversationId && editingId !== c.conversationId"
				@click="open(c)"
			>
				<!-- Deleting asks once, in the row itself, so the question sits next to the conversation it is about. -->
				<div v-if="confirmingId === c.conversationId" class="assistant-confirm" @click.stop>
					<div class="assistant-confirm-title">Delete this conversation?</div>
					<div class="assistant-confirm-text">
						Its messages are removed from your account. The questions it used still count toward this
						month's allotment. This cannot be undone.
					</div>
					<div class="assistant-confirm-actions">
						<v-btn small text :disabled="deleting" @click.stop="confirmingId = null">Cancel</v-btn>
						<v-btn small depressed color="#ba0c2f" dark :loading="deleting" @click.stop="doDelete(c)">
							Delete
						</v-btn>
					</div>
				</div>
				<template v-else>
					<v-list-item-content>
						<v-list-item-title>
							<template v-if="editingId === c.conversationId">
								<v-text-field
									v-model="editingTitle"
									dense
									hide-details
									autofocus
									maxlength="120"
									aria-label="Conversation title"
									@keydown.enter.stop="saveRename(c)"
									@keydown.esc.stop="editingId = null"
									@blur="saveRename(c)"
									@click.stop
								></v-text-field>
							</template>
							<template v-else>{{ c.title }}</template>
						</v-list-item-title>
						<v-list-item-subtitle>{{ meta(c) }}</v-list-item-subtitle>
					</v-list-item-content>
					<v-list-item-action class="assistant-list-actions">
						<AssistantIconButton
							tip="Rename"
							icon
							small
							:aria-label="`Rename ${c.title}`"
							@click.stop="startRename(c)"
						>
							<v-icon>mdi-pencil-outline</v-icon>
						</AssistantIconButton>
						<AssistantIconButton
							tip="Delete"
							icon
							small
							:aria-label="`Delete ${c.title}`"
							@click.stop="confirmingId = c.conversationId"
						>
							<v-icon>mdi-trash-can-outline</v-icon>
						</AssistantIconButton>
					</v-list-item-action>
				</template>
			</v-list-item>
		</v-list>
		<v-btn v-if="conversations.length < total" small text block :loading="loading" @click="more">
			Load more
		</v-btn>
	</div>
</template>

<script>
import { mapState } from "vuex";
import AssistantIconButton from "./AssistantIconButton.vue";

export default {
	name: "ConversationList",
	components: { AssistantIconButton },
	data() {
		return {
			search: this.$store.state.assistant.conversationsSearch || "",
			editingId: null,
			editingTitle: "",
			confirmingId: null,
			deleting: false,
			timer: null,
		};
	},
	computed: {
		...mapState("assistant", {
			conversations: "conversations",
			total: "conversationsTotal",
			loading: "conversationsLoading",
			page: "conversationsPage",
			currentId: "conversationId",
		}),
	},
	beforeDestroy() {
		clearTimeout(this.timer);
	},
	methods: {
		// "Today, 3:18 PM", "Yesterday, 9:32 PM", "Oct 9, 4:35 PM", and the year only when it is not this one.
		formatDate(value) {
			const m = this.moment(value);
			const now = this.moment();
			if (m.isSame(now, "day")) return `Today, ${m.format("h:mm A")}`;
			if (m.isSame(now.clone().subtract(1, "day"), "day")) return `Yesterday, ${m.format("h:mm A")}`;
			return m.format(m.isSame(now, "year") ? "MMM D, h:mm A" : "MMM D, YYYY");
		},
		meta(c) {
			const parts = [
				this.formatDate(c.lastActivityAt),
				`${c.turnCount} ${c.turnCount === 1 ? "question" : "questions"}`,
			];
			if (c.isClosed) parts.push("closed");
			return parts.join(", ");
		},
		debouncedSearch() {
			clearTimeout(this.timer);
			this.timer = setTimeout(() => {
				this.$store.commit("assistant/set_conversations_search", this.search || "");
				this.$store.dispatch("assistant/loadConversations", { page: 1 });
			}, 300);
		},
		more() {
			this.$store.dispatch("assistant/loadConversations", { page: this.page + 1 });
		},
		open(c) {
			if (this.editingId || this.confirmingId === c.conversationId) return;
			this.confirmingId = null;
			this.$store.dispatch("assistant/openConversation", c.conversationId);
		},
		startRename(c) {
			this.confirmingId = null;
			this.editingId = c.conversationId;
			this.editingTitle = c.title;
		},
		// Enter and leaving the field both save; Escape cancels. The first of Enter and blur wins.
		async saveRename(c) {
			if (this.editingId !== c.conversationId) return;
			this.editingId = null;
			const title = (this.editingTitle || "").trim();
			if (title && title !== c.title)
				await this.$store.dispatch("assistant/renameConversation", { id: c.conversationId, title });
		},
		async doDelete(c) {
			this.deleting = true;
			try {
				await this.$store.dispatch("assistant/deleteConversation", c.conversationId);
			} finally {
				this.deleting = false;
				this.confirmingId = null;
			}
		},
	},
};
</script>
